import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  LogoutOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { navSections } from "../../constants/sidebar";
import { Collapse } from "@mui/material";
import SPM_Primary from "../../assets/images/SPM_Primary.png";

import useDeviceId from "@hooks/useDeviceId";
import { ConfirmationModal, useConfirmAction } from "@components/ConfirmationAlert";
import { useLogoutMutation } from "@features/auth/services";
import { handleAsync } from "@utils/helpers";

const Sidebar = () => {
  const deviceId = useDeviceId();
  const navigate = useNavigate();
  const location = useLocation();

  const logoutAction = useConfirmAction<null>({ showSuccessToast: false });


  const isPathActive = (path: string) => location.pathname.startsWith(path);
  const isChildActive = (items?: { path: string }[]) =>
    items?.some((item) => isPathActive(item.path));

  const [openMenus, setOpenMenus] = useState<{
    haveChild: string | null;
    exploreNext: string | null;
  }>({
    haveChild:
      navSections.find((section) =>
        section.items?.some((item) => location.pathname.startsWith(item.path))
      )?.label || null,
    exploreNext: null,
  });

  const toggleMenu = (label: string, hasChild: boolean) => {
    setOpenMenus((prev) => {
      if (hasChild) {
        return { ...prev, haveChild: label };
      } else {
        return { ...prev, exploreNext: prev.exploreNext === label ? null : label };
      }
    });
  };

  const isSectionOpen = (label: string) =>
    openMenus.haveChild === label || openMenus.exploreNext === label;

  const [logOutUser, { status, isLoading }] = useLogoutMutation();

  const handleLogout = async () => {
    console.log("j")
    await handleAsync(
      () => logOutUser({ deviceId: deviceId ?? "" }).unwrap(),
      {
        successMessage: "Logged out successfully",
        onSuccess: () => {
          navigate("/login");
        },
        onError: (error) => {
          console.error(error);
          navigate("/login");
        }
      }
    );
  };

  return (
    <div className="bg-secondary py-4 h-full sidebar">
      <div className="px-5">
        <img src={SPM_Primary} className="w-[80px] mx-auto" alt="Admin Logo" />
      </div>
      <hr className="mt-4" />
      <ul className="text-white py-4 h-[calc(100vh-65px)] overflow-x-hidden">
        {navSections.map((section) => {
          const sectionIsOpen = isSectionOpen(section.label);
          const sectionHasActiveChild = isChildActive(section.items);

          const sectionClass = `flex tracking-wider items-center gap-3 px-3 py-2 hover:bg-primary border-l-[4px] ${sectionHasActiveChild
            ? "bg-primary border-[#fff3dd]"
            : "border-transparent"
            }`;

          if (section.type === "single") {
            return (
              <NavLink
                to={section.path}
                key={section.label}
                className={({ isActive }) =>
                  `text-sm flex tracking-wider items-center gap-3 px-3 py-2 hover:bg-primary border-l-[4px] ${isActive ? "bg-primary border-[#fff3dd]" : "border-transparent"}`
                }
                onClick={() => setOpenMenus({ haveChild: null, exploreNext: null })}
              >
                {section.icon}
                <span className="uppercase">{section.label}</span>
              </NavLink>
            );
          }

          return (
            <div key={section.label}>
              <div
                onClick={() => toggleMenu(section.label, !!sectionHasActiveChild)}
                className={`${sectionClass} justify-between cursor-pointer`}
              >
                <div className="flex items-center gap-3">
                  {section.icon}
                  <span className="uppercase text-sm">{section.label}</span>
                </div>
                {sectionIsOpen
                  ? <KeyboardArrowUpOutlined className="text-sm" />
                  : <KeyboardArrowDownOutlined className="text-sm" />
                }
              </div>

              <Collapse in={sectionIsOpen}>
                <div>
                  {section.items?.map((item) => (
                    <NavLink
                      to={item.path}
                      key={item.label}
                      className={`block py-1.5 text-[13px] hover:bg-primary ${isPathActive(item.path) ? "bg-primary" : ""}`}
                      onClick={() => setOpenMenus({ haveChild: section.label, exploreNext: null })}
                    >
                      <div className="flex items-center gap-2 pl-8">
                        {item.icon}
                        <span className="capitalize">{item.label}</span>
                      </div>
                    </NavLink>
                  ))}
                </div>
              </Collapse>
            </div>
          );
        })}

        <li>
          <button
            className="w-full text-sm duration-300 px-3 py-2.5 text-white tracking-wider hover:bg-primary uppercase flex items-center gap-3 border-l-[6px] border-transparent"
            onClick={() => logoutAction.request(null)}
          >
            <LogoutOutlined />
            {status === "pending" ? "Loading..." : "Logout"}
          </button>
        </li>
      </ul>

      <ConfirmationModal
        show={logoutAction.state.open}
        onClose={logoutAction.close}
        onConfirm={() => logoutAction.confirm(handleLogout)}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        confirmButtonText="Logout"
        variant="warning"
        isLoading={status === "pending" || isLoading}
      />
    </div>
  );
};

export default Sidebar;